/**
 * Pointer interaction and lifetime for the KB graph canvas.
 *
 * Owns the camera (pan/zoom), hit-testing, hover, selection and the listeners.
 * The painting is in kb_graph_draw.ts and is a pure function of this state — so
 * everything here is about pointers and teardown, and nothing here decides what
 * a node looks like.
 *
 * Why canvas rather than SVG: the live KB's 3,427 nodes plus 6,507 edges is
 * 10,000 DOM elements, and every hover would walk that tree. Canvas draws the scene in one
 * pass; the cost is hit-testing by hand, which is a loop over points we hold.
 */

import { esc } from '../../shared/rendering.js'
import { layoutBounds } from './kb_graph_bounds.js'
import { adjacency, paintGraph, typeColours, type GraphScene } from './kb_graph_draw.js'
import { type LayoutNode } from './kb_graph_layout.js'

const HOVER_RADIUS = 9
const FIT_PADDING = 40
const ZOOM_STEP = 1.12
/** Pointer travel, in px, above which a click is treated as the end of a drag. */
const DRAG_SLOP = 4

export type { GraphScene }

export type GraphCanvas = {
  destroy: () => void
  /** Set or clear the selection from outside — e.g. when the panel is closed. */
  select: (id: string | null) => void
}

type SceneNode = { id: string; type: string; title: string }

/**
 * Wire the canvas and draw it.
 *
 * Returns a handle whose `destroy` removes every listener — including the one on
 * `window` and the ResizeObserver, which outlive the canvas element and would
 * otherwise keep firing against a detached node after the user navigates away.
 */
export function mountGraphCanvas(
  canvas: HTMLCanvasElement,
  tip: HTMLElement | null,
  scene: GraphScene,
  positions: readonly LayoutNode[],
  onSelect: (id: string | null) => void,
): GraphCanvas {
  const pos = new Map(positions.map((p) => [p.id, p]))
  const colours = typeColours(scene)
  const neighbours = adjacency(scene)
  const bounds = layoutBounds(positions)

  let scale = 1
  let panX = 0
  let panY = 0
  let hover: string | null = null
  let selected: string | null = null
  /** Set once the user pans or zooms; after that the view is theirs, not ours. */
  let adjusted = false

  const draw = (): void => {
    paintGraph(canvas, scene, pos, { scale, panX, panY, hover, selected }, colours, neighbours)
  }

  /** Scale and centre the whole graph inside the canvas's CURRENT box. */
  function fit(): void {
    const w = canvas.clientWidth
    const h = canvas.clientHeight
    if (w <= 0 || h <= 0) return
    const gw = Math.max(1, bounds.maxX - bounds.minX)
    const gh = Math.max(1, bounds.maxY - bounds.minY)
    scale = Math.min((w - FIT_PADDING * 2) / gw, (h - FIT_PADDING * 2) / gh)
    panX = (w - gw * scale) / 2 - bounds.minX * scale
    panY = (h - gh * scale) / 2 - bounds.minY * scale
  }

  /** Nearest node within HOVER_RADIUS of a canvas point, or null. */
  function nodeAt(cx: number, cy: number): SceneNode | null {
    let best: SceneNode | null = null
    let bestD = HOVER_RADIUS
    for (const n of scene.nodes) {
      const p = pos.get(n.id)
      if (!p) continue
      const d = Math.hypot(p.x * scale + panX - cx, p.y * scale + panY - cy)
      if (d < bestD) {
        bestD = d
        best = n
      }
    }
    return best
  }

  let dragging = false
  let lastX = 0
  let lastY = 0
  let moved = 0

  const onMove = (ev: MouseEvent): void => {
    const rect = canvas.getBoundingClientRect()
    const cx = ev.clientX - rect.left
    const cy = ev.clientY - rect.top
    if (dragging) {
      adjusted = true
      panX += ev.clientX - lastX
      panY += ev.clientY - lastY
      moved += Math.abs(ev.clientX - lastX) + Math.abs(ev.clientY - lastY)
      lastX = ev.clientX
      lastY = ev.clientY
      draw()
      return
    }
    const hit = nodeAt(cx, cy)
    if ((hit?.id ?? null) !== hover) {
      hover = hit?.id ?? null
      canvas.style.cursor = hit ? 'pointer' : 'grab'
      draw()
    }
    if (!tip) return
    if (hit) {
      tip.hidden = false
      tip.style.left = `${String(Math.round(cx + 14))}px`
      tip.style.top = `${String(Math.round(cy + 14))}px`
      tip.innerHTML = `<b>${esc(hit.title)}</b><span>${esc(hit.type)}</span>`
    } else {
      tip.hidden = true
    }
  }

  const onDown = (ev: MouseEvent): void => {
    dragging = true
    moved = 0
    lastX = ev.clientX
    lastY = ev.clientY
  }

  const onUp = (): void => {
    dragging = false
  }

  const onClick = (ev: MouseEvent): void => {
    // A drag that happens to end over a node is not a click on it; without this
    // every pan finishing on a dot would change the selection.
    if (moved > DRAG_SLOP) return
    const rect = canvas.getBoundingClientRect()
    const hit = nodeAt(ev.clientX - rect.left, ev.clientY - rect.top)
    // Clicking empty space clears the selection — the obvious way out, so the
    // panel never depends on the close button alone.
    const next = hit?.id ?? null
    if (next === selected) return
    selected = next
    draw()
    onSelect(next)
  }

  const onWheel = (ev: WheelEvent): void => {
    ev.preventDefault()
    const rect = canvas.getBoundingClientRect()
    const cx = ev.clientX - rect.left
    const cy = ev.clientY - rect.top
    const factor = ev.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP
    // Zoom about the cursor rather than the origin, or whatever you are
    // pointing at slides out from under you as you scroll.
    adjusted = true
    panX = cx - (cx - panX) * factor
    panY = cy - (cy - panY) * factor
    scale *= factor
    draw()
  }

  const onLeave = (): void => {
    dragging = false
    if (tip) tip.hidden = true
    if (hover !== null) {
      hover = null
      draw()
    }
  }

  // Fitted from a ResizeObserver rather than once at mount.
  //
  // Mounting reads clientWidth in the same tick the markup is inserted, and on a
  // cold load that can measure a box the browser has not laid out yet — the
  // graph then renders scaled to a 300x150 default in the corner and, since
  // nothing recomputed it, stays there. The observer fires with the real box
  // whenever there is one, covering first layout, window resizes and the detail
  // panel opening beside the canvas, with no guessing about which happened.
  //
  // It stops refitting once the user pans or zooms: silently resetting someone's
  // view because the window moved is worse than an imperfect fit.
  const observer = new ResizeObserver(() => {
    if (!adjusted) fit()
    draw()
  })
  observer.observe(canvas)

  canvas.style.cursor = 'grab'
  canvas.addEventListener('mousemove', onMove)
  canvas.addEventListener('mousedown', onDown)
  canvas.addEventListener('mouseleave', onLeave)
  canvas.addEventListener('click', onClick)
  canvas.addEventListener('wheel', onWheel, { passive: false })
  window.addEventListener('mouseup', onUp)
  fit()
  draw()

  return {
    select: (id: string | null): void => {
      if (id === selected) return
      selected = id
      draw()
    },
    destroy: (): void => {
      canvas.removeEventListener('mousemove', onMove)
      canvas.removeEventListener('mousedown', onDown)
      canvas.removeEventListener('mouseleave', onLeave)
      canvas.removeEventListener('click', onClick)
      canvas.removeEventListener('wheel', onWheel)
      window.removeEventListener('mouseup', onUp)
      observer.disconnect()
    },
  }
}
